import { memo } from "react";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";

import { Finding } from "stores/FindingStore";
import { IgnoreType, RuleType } from "stores/IgnoreStore";

import { FOTS } from "utility/typescript";

import RegexInput from "./RegexInput";

// -----------------------------------------------------------------------------

export interface IgnoreTypeData {
  ignoreType: IgnoreType;
  ruleType: RuleType;
  assetRegex: string;
  ruleTypeError: boolean;
  regexError: boolean;
}

const IgnoreTypePicker = memo(function IgnoreTypePicker({
  data,
  setData,
  finding,
}: IgnoreTypePickerProps): JSX.Element {
  const { ignoreType, ruleType, ruleTypeError } = data;

  return (
    <>
      <Typography sx={{ mb: 1 }}>Ignore type:</Typography>
      <FormControl>
        <RadioGroup
          value={ignoreType}
          onChange={(e: FOTS) => {
            setData({
              ...data,
              ignoreType: e?.target?.value as IgnoreType,
              ruleTypeError: false,
              regexError: false,
            });
          }}
        >
          <FormControlLabel
            value="instance"
            control={<Radio />}
            sx={{ alignItems: "flex-start", mr: 0 }}
            label={
              <Box>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Instance
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Selecting this option will only ignore this instance and not
                  any future findings
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="rule"
            control={<Radio />}
            sx={{ alignItems: "flex-start", mt: 1, mr: 0 }}
            label={
              <Box>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Create Rule
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Selecting this option will ignore any current and future
                  findings by creating an auto ignore rule
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>

      <Box sx={{ ml: 3 }}>
        <FormControl
          fullWidth
          sx={{ mt: 2 }}
          disabled={ignoreType !== "rule"}
          error={ruleTypeError}
        >
          <InputLabel id="ignore-rule-type-select-label" required>
            Rule type
          </InputLabel>
          <Select
            labelId="ignore-rule-type-select-label"
            id="ignore-rule-type-select"
            required
            value={ruleType}
            label="Rule type"
            onChange={(e: FOTS) => {
              setData({
                ...data,
                ruleType: e?.target?.value as RuleType,
                ruleTypeError: false,
                regexError: false,
              });
            }}
          >
            <MenuItem value="finding">Finding type</MenuItem>
            <MenuItem value="asset">Asset ID</MenuItem>
            <MenuItem value="regex">Asset Regex</MenuItem>
          </Select>
          {ruleTypeError ? <FormHelperText>Required</FormHelperText> : null}
        </FormControl>

        <Collapse
          in={ignoreType === "rule" && ruleType === "finding"}
          mountOnEnter
          unmountOnExit
        >
          <Typography variant="body1" sx={{ mt: 1, color: "text.secondary" }}>
            You are choosing to ignore all current and future findings of type{" "}
            <Box
              component="span"
              sx={{ fontWeight: "bold", wordBreak: "break-word" }}
            >
              {finding.name}
            </Box>
          </Typography>
        </Collapse>
        <Collapse
          in={ignoreType === "rule" && ruleType === "asset"}
          mountOnEnter
          unmountOnExit
        >
          <Typography variant="body1" sx={{ mt: 1, color: "text.secondary" }}>
            You are choosing to ignore all current and future findings with the
            asset id:{" "}
            <Box
              component="span"
              sx={{ fontWeight: "bold", wordBreak: "break-word" }}
            >
              {finding.asset}
            </Box>
          </Typography>
        </Collapse>
        <Collapse
          in={ignoreType === "rule" && ruleType === "regex"}
          mountOnEnter
          unmountOnExit
        >
          <RegexInput data={data} setData={setData} finding={finding} />
        </Collapse>
      </Box>
    </>
  );
});

export default IgnoreTypePicker;

// -----------------------------------------------------------------------------

interface IgnoreTypePickerProps {
  data: IgnoreTypeData;
  setData: (data: IgnoreTypeData) => void;
  finding: Finding;
}
